// `beforeinstallprompt` fires once per page load, early — Chrome can dispatch it
// before React has hydrated, and it never fires again for that load. We register
// the listener at module scope from the entrypoint (before `createInertiaApp`) so
// the event is captured whenever it lands, and stash it for the later-mounted
// install banner to consume. See issue #77.

// `beforeinstallprompt` is not part of the standard DOM typings.
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

// Re-dispatched on `window` when we capture the native event, so a banner that
// mounts *after* the native event already fired still learns it is installable.
export const PWA_INSTALL_AVAILABLE_EVENT = "pwa:install-available"

let stashedPrompt: BeforeInstallPromptEvent | null = null

// The captured native event, or null if Chrome has not offered installation.
export function getStashedInstallPrompt(): BeforeInstallPromptEvent | null {
  return stashedPrompt
}

// A `beforeinstallprompt` event can only be used once; drop the stash after the
// banner has prompted with it (or the app was installed).
export function clearStashedInstallPrompt(): void {
  stashedPrompt = null
}

// Registered once, at module load from the entrypoint — before React hydrates —
// so the install event is never lost to the hydration race.
export function capturePwaInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault()
    stashedPrompt = event as BeforeInstallPromptEvent
    window.dispatchEvent(new CustomEvent(PWA_INSTALL_AVAILABLE_EVENT))
  })
}
